import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	LoadTimeRating,
	type ContentMetricRow,
} from '../content-metrics/components/ContentMetricsTable.tsx';
import type { useContentMetricsQuery } from './__generated__/useContentMetricsQuery.graphql.ts';

const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_DATE_RANGE_DAYS = 7;

// Keep default date range values stable across renders so query variables
// are deterministic and don't trigger accidental refetches.
const DEFAULT_END_TIMESTAMP = new Date().toISOString();
const DEFAULT_START_TIMESTAMP = new Date(
	Date.now() - DEFAULT_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000,
).toISOString();

const mapOperations = (
	operations: ReadonlyArray<
		{ operation?: string | null; targetType?: string | null } | null | undefined
	>,
) =>
	operations.map((operation) => ({
		operation: operation?.operation ?? '',
		targetType: operation?.targetType ?? '',
	}));

const mapLoadTimeRating = (loadTimeRating?: string | null): LoadTimeRating => {
	switch (loadTimeRating) {
		case 'FAST':
			return LoadTimeRating.FAST;
		case 'SLOW':
			return LoadTimeRating.SLOW;
		default:
			return LoadTimeRating.MODERATE;
	}
};

/**
 * Fetches content metrics using Relay GraphQL query.
 *
 * @param searchQuery - Optional search term to filter content by title
 * @returns Object containing data (array of ContentMetricRow)
 *
 * This hook:
 * - Queries the confluence_contentPerformanceList GraphQL endpoint via Relay
 * - Uses last 7 days as the default time window
 * - Supports server-side search filtering via searchTerm parameter
 * - Returns paginated results (first 15 items by default)
 */
export const useContentMetrics = (searchQuery: string) => {
	const { cloudId } = useSessionData();

	// Date range defaults.
	// NOTE: do not compute with `new Date()` here; see module-level constants above.
	const startTimestamp = DEFAULT_START_TIMESTAMP;
	const endTimestamp = DEFAULT_END_TIMESTAMP;

	const variables = useMemo(
		() => ({
			cloudId,
			first: DEFAULT_PAGE_SIZE,
			searchTerm: searchQuery?.trim() || undefined,
			startTimestamp,
			endTimestamp,
		}),
		[cloudId, searchQuery, startTimestamp, endTimestamp],
	);

	const { data, loading, error } = useQueryInterop<
		useContentMetricsQuery['response'],
		useContentMetricsQuery['variables']
	>(
		graphql`
			query useContentMetricsQuery(
				$cloudId: ID!
				$first: Int!
				$after: String
				$searchTerm: String
				$filters: ConfluenceContentPerformanceFilters
				$startTimestamp: String!
				$endTimestamp: String
			) {
				confluence_contentPerformanceList(
					cloudId: $cloudId
					first: $first
					after: $after
					searchTerm: $searchTerm
					filters: $filters
					startTimestamp: $startTimestamp
					endTimestamp: $endTimestamp
				) {
					nodes {
						id
						content {
							contentTitle
							link
							contentId
						}
						loadTimeRating
						medianLoadTime
						views
						space {
							id
							name
						}
						contentOwner {
							id
							displayName
						}
						operations {
							operation
							targetType
						}
					}
					pageInfo {
						hasNextPage
						endCursor
						startCursor
						hasPreviousPage
					}
					totalCount
				}
			}
		`,
		{
			variables,
			skip: !cloudId,
			fetchPolicy: 'network-only',
		},
	);

	const items: ContentMetricRow[] = useMemo(() => {
		const nodes = data?.confluence_contentPerformanceList?.nodes ?? [];

		return nodes.map((node) => ({
			id: node?.id ?? '',
			content: {
				contentTitle: node?.content?.contentTitle ?? '',
				link: node?.content?.link ?? '',
				contentId: node?.content?.contentId ?? '',
			},
			loadTimeRating: mapLoadTimeRating(node?.loadTimeRating),
			medianLoadTime: node?.medianLoadTime ?? 0,
			views: node?.views ?? 0,
			space: {
				id: node?.space?.id ?? '',
				name: node?.space?.name ?? '',
			},
			contentOwner: {
				id: node?.contentOwner?.id ?? '',
				displayName: node?.contentOwner?.displayName ?? '',
			},
			operations: mapOperations(node?.operations ?? []),
			actions: '',
		}));
	}, [data]);

	return {
		data: items,
		loading,
		error,
	};
};
